<template>
  <v-flex xs12 sm6 md6 lg4 xl3 style="align-items: stretch">
    <v-card style="height: 100%">
      <v-alert :color="cardColour" :value="true" class="card-limit">
        <v-tooltip top>
          <v-icon medium slot="activator" class="mr-1 icon-style">{{cardIcon}}</v-icon>
          <span v-html="toolTipText"></span>
        </v-tooltip>
        <div class="headline arrow-align">{{title}} <v-icon v-if="feed.requiredEngagement" color="white">mdi-flag</v-icon></div>
        <div class="right-align chevron-align" @click="goToDetails(feed)"><v-icon color="white">mdi-chevron-right</v-icon></div>
      </v-alert>
      <v-card-text>
        <v-layout row wrap class="caption mb-1" v-if="allowRun">
          <v-flex xs6 pr-1 class="right-align">{{$t('feeds.next_schedule_date')}}</v-flex>
          <v-flex xs6 pl-0 v-if="!feedRunDisabled" :class="[feedDateMismatch ? 'font-red' : 'font-black']"> {{formatNextTriggerDate}}</v-flex>
          <v-flex xs6 pl-0 v-if="feedRunDisabled" class="font-black" v-else>{{$t('hyphen')}}</v-flex>
        </v-layout>
        <v-layout  wrap justify-space-between>
          <v-flex xs12 sm6>
            <div class="headline" v-if="feed.processFromDate !== ''">{{feed.processFromDate | date}}</div>
            <div class="headline" v-else>{{$t('hyphen')}}</div>
            <div class="caption mb-1">{{$t('feeds.process_from')}}</div>
          </v-flex>
          <v-flex xs12 sm6>
            <div class="headline" v-if="feed.processToDate !== ''">{{feed.processToDate | date}}</div>
            <div class="headline" v-else>{{$t('hyphen')}}</div>
            <div class="caption mb-1">{{$t('feeds.process_to')}}</div>
          </v-flex>
        </v-layout>
        <div class="headline" v-if="feed.processStartTimestamp !== 0">{{feed.processStartTimestamp | time}}</div>
        <div class="headline" v-else>{{$t('hyphen')}}</div>
        <div class="caption mb-1">{{$t('feeds.feed_start')}}</div>
        <template v-if="!feedProcessing">
          <div class="headline" v-if="feed.processEndTimestamp !== 0">{{feed.processEndTimestamp | time}}</div>
          <div class="headline" v-else>{{$t('hyphen')}}</div>
          <div class="caption mb-1">{{$t('feeds.feed_end')}}</div>
          <v-layout row>
            <v-flex xs6 pb-0>
              <div class="headline">{{feed.recordsProcessed}}</div>
            </v-flex>
            <v-flex xs6 pb-0>
              <div class="headline">{{feed.recordsErrored}}</div>
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs6 pt-0>
              <div class="caption mb-1">{{$t('feeds.processed')}}</div>
            </v-flex>
            <v-flex xs6 pt-0>
              <div class="caption mb-1">{{$t('feeds.errors')}}</div>
            </v-flex>
          </v-layout>
          <v-layout v-if="allowRun">
            <v-flex xs4 pt-2>
              <span class="run-text" v-if="feedWithDateRange && !feedRunDisabled">{{$t('feeds.run-from')}}</span>
            </v-flex>
            <v-flex xs4 pt-2>
              <date-picker class="run-date" v-if="feedWithDateRange && !feedRunDisabled" :date="runDate" :maxEnd="today" @updated="updateRunDate"></date-picker>
            </v-flex>
            <v-flex xs4 pt-0 class="run-btn">
              <v-btn id="feed-run-comment" :disabled="feedRunDisabled" color="primary" outline  @click="showFeedCommentsDialog">{{$t("feeds.re-run")}}
                <feed-run-comment :dialog.sync="modal" :feed="feed" :feedTitle="title" :runDate="runDate"/>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout row wrap justify-end v-if="allowRun">
            <v-flex xs12 md2 pt-0 pb-0>
              <v-switch id="feed-active" aria-label="feed-active" role="switch" slot="activator" color="green" hide-details v-model="isActive"></v-switch>
            </v-flex>
          </v-layout>
        </template>
        <template v-else>
          <div class="headline mt-2">{{$t("feeds.in_progress")}}</div>
        </template>
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
  import DateFormat from '../../mixins/DateFormat'
  import DatePicker from '../date/DatePicker'
  import moment from 'moment-es6'
  import { mapState, mapActions } from 'vuex'
  import { RUN_FEED, UPDATE_FEED_STATUS } from '../../store/action-types'
  import { SET_FEED_ACTIVE_TOGGLE } from '../../store/mutation-types'
  import FeedRunComment from './FeedRunComment'

  export default {
    name: 'daily-feed',
    props: ['feed', 'allowRun', 'feedDate'],
    mixins: [DateFormat],
    data () {
      return {
        now: moment().format('YYYY-MM-DD'),
        today: moment().format('YYYY-MM-DD'),
        runDate: moment().format('YYYY-MM-DD'),
        yesterday: moment().subtract(1, 'days').format('YYYY-MM-DD'),
        modal: false
      }
    },
    ...mapState({
      feedStatus: state => state.feeds.feedStatus
    }),
    computed: {
      cardColour () {
        if (!this.feed.processActive) {
          return 'grey'
        } else if (!this.feed.processStartTimestamp) {
          return 'brown'
        } else if (!this.feed.processEndTimestamp) {
          return 'blue'
        } else if (this.feed.success && this.feed.recordsErrored === 0) {
          return 'green'
        } else if (this.feed.success && this.feed.recordsErrored > 0) {
          return 'amber'
        } else return 'red'
      },
      cardIcon () {
        if (!this.feed.processActive) {
          return 'block'
        } else if (!this.feed.processStartTimestamp) {
          return 'alarm'
        } else if (!this.feed.processEndTimestamp) {
          return 'info_outline'
        } else if (this.feed.success && this.feed.recordsErrored === 0) {
          return 'check'
        } else if (this.feed.success && this.feed.recordsErrored > 0) {
          return 'priority_high'
        } else return 'warning'
      },
      toolTipText () {
        if (!this.feed.processActive) {
          return this.$t(`feeds.blocked_feed_tooltip`)
        } else if (!this.feed.processStartTimestamp) {
          return this.$t(`feeds.not_yet_started_feed_tooltip`)
        } else if (!this.feed.processEndTimestamp) {
          return this.$t(`feeds.in_progress_feed_tooltip`)
        } else if (this.feed.success && this.feed.recordsErrored === 0) {
          return this.$t(`feeds.successful_feed_tooltip`)
        } else if (this.feed.success && this.feed.recordsErrored > 0) {
          return this.$t(`feeds.warning_feed_tooltip`)
        } else return this.$t(`feeds.error_feed_tooltip`)
      },
      formatNextTriggerDate () {
        return moment(this.feed.nextTriggerDate).format('dddd DD/MM/YYYY hh:mm')
      },
      feedRunDisabled () {
        return !this.feed.processActive
      },
      feedProcessing () {
        return this.feed.processStartTimestamp && !this.feed.processEndTimestamp
      },
      feedAlertType () {
        if (this.feed.processEndTimestamp === null) { return 'info' }
        if (this.feed.success && this.feed.recordsErrored === 0) { return 'success' }
        if (this.feed.success && this.feed.recordsErrored > 0) { return 'warning' }
        return 'error'
      },
      title () {
        let key = `feeds.name.${this.feed.processId}.${this.feed.scheduleId}`
        if (this.$i18n.keyExists(key)) {
          return this.$t(key)
        }
        return `${this.feed.scheduleId}-${this.feed.processId}`
      },
      feedWithDateRange () {
        if (this.feed.processScheduleOffsetDays < 0) { return true }
        return false
      },
      feedDateMismatch () {
        let feedDateFormatted = moment(this.feedDate)
        return moment(this.feed.lastUpdatedTimestamp).isBefore(feedDateFormatted)
      },
      isActive: {
        get () {
          return this.feed.processActive
        },
        set (value) {
          this.$store.commit(SET_FEED_ACTIVE_TOGGLE, {feed: this.feed, active: value})
          this.$store.dispatch(UPDATE_FEED_STATUS, {scheduleId: this.feed.scheduleId, active: value})
        }
      }
    },
    methods: {
      ...mapActions({
        runFeed: RUN_FEED
      }),
      updateRunDate (newDate) {
        this.runDate = newDate
      },
      doRunFeed (feed) {
        if (this.feed.processScheduleOffsetDays < 0) {
          this.runFeed({processId: this.feed.processId, scheduleId: this.feed.scheduleId, date: this.today, fromDate: this.runDate})
        } else {
          this.runFeed({processId: this.feed.processId, scheduleId: this.feed.scheduleId, date: this.today, fromDate: null})
        }
      },
      showFeedCommentsDialog () {
        this.modal = true
      },
      goToDetails (feed) {
        this.$router.push({name: 'FeedRequestBreakdownInfo', params: {id: feed.scheduleId}})
      }
    },
    components: {
      DatePicker, FeedRunComment
    }
  }
</script>

<style scoped>
  div >>> div.card > div.alert > div {
    overflow: hidden;
  }
  .run-btn {
    text-align: center;
  }
  .run-text {
    float: right;
  }
  .right-align {
    text-align: right;
  }
  .right-align:hover {
    cursor: pointer;
  }
  .run-date {
    left: 13px;
  }
  .card-limit {
    max-width: 95vw;
    overflow: hidden;
  }
  .arrow-align {
    float: left;
  }
  .font-red {
    color: red;
    font-weight: bold;
  }
  .font-black {
    color: black;
    font-weight: bold;
  }
  .icon-style {
    color:white;
    float: left;
    padding-top: 3px;
    padding-right: 3px;
  }
  .chevron-align{
    padding-top: 5px;
  }
  .align-center {
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/monitor/DailyFeed.vue